import type { TreeNode } from "@atlas-int/ui";

/**
 * Location hierarchy level constants.
 * Values match the canonical LocationMetadatumLevel enum from @atlas-int/data
 * so callers can pass either these constants or the enum — they are interchangeable.
 */
export const LocationItemLevel = {
  country: "country",
  subregion: "subregion",
  region: "region",
  world: "world",
} as const;
export type LocationItemLevel = (typeof LocationItemLevel)[keyof typeof LocationItemLevel];

export interface LocationItem {
  id: string;
  name_short_en: string;
  name_en?: string;
  iso3Code?: string;
  locationLevel: string;
  hybrid_level_1?: string;
  hybrid_level_2?: string;
  countryId?: string;
  isSticky?: boolean;
  [key: string]: unknown;
}

export const buildLocationTree = (
  flatLocations: LocationItem[]
): TreeNode[] => {
  const tree: TreeNode[] = [];
  const nodeMap = new Map<string, TreeNode>();

  // First pass: Create all nodes
  flatLocations.forEach((location) => {
    const node: TreeNode = {
      id: location.id,
      name: location.name_short_en,
      children: [],
      data: location,
      ...location,
    };
    nodeMap.set(location.id, node);
  });

  // Second pass: Build tree hierarchy
  flatLocations.forEach((location) => {
    const node = nodeMap.get(location.id);
    if (!node) return;

    if (location.locationLevel === LocationItemLevel.world) {
      // World is root level
      tree.push(node);
    } else if (location.isSticky === undefined && location.locationLevel === LocationItemLevel.country && !location.hybrid_level_2) {
      // Top-level country items (like Services Partners) are root level
      tree.push(node);
    } else if (location.locationLevel === LocationItemLevel.region) {
      // Regions are root level (after world)
      tree.push(node);
    } else if (location.locationLevel === LocationItemLevel.subregion) {
      // Subregions are children of regions
      const parentId = location.hybrid_level_1;
      const parent = nodeMap.get(parentId!);
      if (parent) {
        parent.children!.push(node);
      }
    } else if (location.locationLevel === LocationItemLevel.country) {
      // Countries are children of subregions
      const parentId = location.hybrid_level_2;
      const parent = nodeMap.get(parentId!);
      if (parent) {
        parent.children!.push(node);
      }
    }
  });

  return tree;
};

export const getInitialExpandedLocationIds = (
  _flatLocations: LocationItem[],
  selectedLocation?: LocationItem
): Set<string> => {
  const expandedIds = new Set<string>();

  if (!selectedLocation) return expandedIds;

  // Expand the full path to the selected location
  if (selectedLocation.locationLevel === LocationItemLevel.country) {
    // If a country is selected, expand its region and subregion
    if (selectedLocation.hybrid_level_1) {
      expandedIds.add(selectedLocation.hybrid_level_1);
    }
    if (selectedLocation.hybrid_level_2) {
      expandedIds.add(selectedLocation.hybrid_level_2);
    }
  } else if (selectedLocation.locationLevel === LocationItemLevel.subregion) {
    // If a subregion is selected, expand its region
    if (selectedLocation.hybrid_level_1) {
      expandedIds.add(selectedLocation.hybrid_level_1);
    }
    // Also expand the subregion itself to show its countries
    expandedIds.add(selectedLocation.id);
  } else if (selectedLocation.locationLevel === LocationItemLevel.region) {
    // If a region is selected, expand it
    expandedIds.add(selectedLocation.id);
  }

  return expandedIds;
};

export const locationSearchFunction = (
  item: LocationItem,
  searchTerm: string
): boolean => {
  const searchString = `${item.name_short_en} ${item.iso3Code || ""}`.toLowerCase();
  return searchString.includes(searchTerm);
};

export const getLabelForLocation = (item: LocationItem): string => {
  return item.name_short_en;
};
